import React, {FC, useState} from 'react';
import {useForm} from 'react-hook-form';
import {PageTitle} from '../../components/page-title/page-title.component';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../../contexts/UserContext';
import {AuthPageLayout} from '../../components/auth-page-layout/auth-page-layout.component';
import {InputSubmitButton} from '../../components/input-submit-btn/input-submit-btn.component';
import { ApiError } from '../../services/api/core/ApiError';

type LoginFormValues = {
  email: string;
  password: string;
  stayLoggedIn: boolean;
};

export const Login: FC = () => {
  const navigate = useNavigate();
  const {login, loggingIn} = useAuth();

  const [responseError, setResponseError] = useState<string>();
  const [stayLoggedIn, setStayLoggedIn] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<LoginFormValues>();

  const onSubmit = handleSubmit(async data => {
    if (submitting) { 
      return;
    }
    try {
      setSubmitting(true);
      setResponseError(undefined);
      await login(data.email, data.password, stayLoggedIn);
    } catch (e:any) {
      if (e instanceof ApiError && (e.status === 400 || e.status === 403)) { 
        setResponseError('Your email address or password is not correct, please try again or reset your password.');
      } else { 
        setResponseError('Something went wrong.')
      }
    } finally { 
      setSubmitting(false);
    }
  });

  return (
    <AuthPageLayout padTop>
      <PageTitle>Welcome</PageTitle>

      {loggingIn ? (
        <h2>Logging in</h2>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4 my-2">
          <input
            aria-label="Email Address"
            aria-invalid={errors.email ? 'true' : 'false'}
            type="email"
            placeholder="Email Address"
            className={`w-full bg-gray-200 rounded-md px-3 py-4 ${errors?.email && 'border border-trak-red'}`}
            {...register('email', {
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
              required: {
                value: true,
                message: 'Email address required',
              },
            })}
          />
          {errors?.email && <p className="text-trak-red whitespace-normal">{errors.email.message}</p>}

          <input
            aria-label="Password"
            aria-invalid={errors.password ? 'true' : 'false'}
            type="password"
            placeholder="Password"
            className={`w-full bg-gray-200 rounded-md px-3 py-4 ${errors?.password && 'border border-trak-red'}`}
            {...register('password', {
              required: {
                value: true,
                message: 'Password required',
              },
            })}
          />

          {errors?.password && <p className="text-trak-red whitespace-normal">{errors.password.message}</p>}
          
          <div className="flex flex-row items-center justify-between my-4 md:my-2 gap-1">
            <label className="flex flex-row gap-1 items-center">
              <input type="checkbox" {...register('stayLoggedIn')} onChange={() => setStayLoggedIn(prev => !prev)} checked={stayLoggedIn} />
              <p className="whitespace-normal text-black text-sm sm:text-base md:text-lg">Stay logged in?</p>
            </label>
            <button
              onMouseDown={() => {
                navigate('forgot-password');
              }}>
              <p className="text-black whitespace-normal text-right text-sm sm:text-base md:text-lg">Forgotten password?</p>
            </button>
          </div>

          {responseError && <p className="text-trak-red whitespace-normal">{responseError}</p>}

          <InputSubmitButton value="Login" />
        </form>
      )}
    </AuthPageLayout>
  );
};
